import { AccommodationWithDays } from './timelineMapUtils';
import { EngagementFieldsFragment } from '@flashpack/graphql';

interface EngagementWithDayNumber extends EngagementFieldsFragment {
  dayNumber: number;
}

// Helper function to create GeoJSON for a line
export const createLineGeoJSON = (coordinates: [number, number][]) => {
  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates,
        },
      },
    ],
  };
};

function calculateAngle(x1: number, y1: number, x2: number, y2: number): number {
  // Calculate the differences
  const dx = x2 - x1;
  const dy = y2 - y1;

  // Calculate the angle in radians using atan2
  const angleRadians = Math.atan2(dy, dx);

  // Convert to degrees
  let angleDegrees = angleRadians * (180 / Math.PI);

  // Convert from counterclockwise to clockwise
  // This is done by taking the negative and then normalizing
  angleDegrees = (-angleDegrees + 360) % 360;

  return angleDegrees;
}

function interpolatePoint(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  fraction: number,
): [number, number] {
  // Linear interpolation formula: p = p1 + fraction * (p2 - p1)

  // Ensure all values are treated as numbers
  const x = Number(x1) + (Number(x2) - Number(x1)) * Number(fraction);
  const y = Number(y1) + (Number(y2) - Number(y1)) * Number(fraction);

  return [x, y];
}

// Helper function to create GeoJSON for arrow symbols
export const createArrowSymbolsGeoJSON = (coordinates: [number, number][]) => {
  interface ArrowFeature {
    type: 'Feature';
    properties: {
      angle: number;
    };
    geometry: {
      type: 'Point';
      coordinates: [number, number];
    };
  }

  const features: ArrowFeature[] = [];

  // Create symbols at the third and two thirds of the way through each line segment
  for (let i = 0; i < coordinates.length - 1; i++) {
    const start = coordinates[i];
    const end = coordinates[i + 1];

    // Calculate third mark
    const thirdMark = interpolatePoint(start[0], start[1], end[0], end[1], 1 / 3);

    // Calculate two thirds mark
    const twoThirdsMark = interpolatePoint(start[0], start[1], end[0], end[1], 2 / 3);

    // Calculate angle (in degrees) for the arrow to point toward the second coordinate
    const angle = calculateAngle(start[0], start[1], end[0], end[1]);

    features.push({
      type: 'Feature' as const,
      properties: {
        angle: angle,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: thirdMark,
      },
    });

    features.push({
      type: 'Feature' as const,
      properties: {
        angle: angle,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: twoThirdsMark,
      },
    });
  }

  return {
    type: 'FeatureCollection' as const,
    features,
  };
};

// Create GeoJSON for engagement labels
export const createEngagementLabelGeoJSON = (engagements: EngagementWithDayNumber[]) => {
  const features = engagements
    .filter(
      (engagement) =>
        engagement.startMapLocation?.longitude && engagement.startMapLocation?.latitude,
    )
    .map((engagement) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [
          parseFloat(String(engagement.startMapLocation!.longitude)),
          parseFloat(String(engagement.startMapLocation!.latitude)),
        ],
      },
      properties: {
        title: engagement.activity?.websiteDescription,
        id: engagement.id,
      },
    }));

  return {
    type: 'FeatureCollection' as const,
    features,
  };
};

// Create GeoJSON for accommodation labels
export const createAccommodationLabelGeoJSON = (
  accommodations: AccommodationWithDays[],
  selectedDay: number | null,
) => {
  const features = accommodations
    .filter(
      (accommodation) =>
        accommodation.location?.longitude &&
        accommodation.location?.latitude &&
        // When a specific day is selected, only show accommodations active on that day
        (selectedDay === null || accommodation.daysActive.includes(selectedDay)),
    )
    .map((accommodation) => {
      const dayRange =
        accommodation.daysActive.length > 1
          ? `Days ${Math.min(...accommodation.daysActive)}-${Math.max(...accommodation.daysActive)}`
          : `Day ${accommodation.daysActive[0]}`;

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [
            parseFloat(String(accommodation.location.longitude)),
            parseFloat(String(accommodation.location.latitude)),
          ],
        },
        properties: {
          title: accommodation.location.name,
          dayRange,
          id: accommodation.id,
          isSelected: selectedDay
            ? accommodation.daysActive.includes(selectedDay)
            : false,
        },
      };
    });

  return {
    type: 'FeatureCollection' as const,
    features,
  };
};

// Create GeoJSON for engagement markers
export const createEngagementMarkersGeoJSON = (
  engagements: EngagementWithDayNumber[],
  selectedDay: number | null,
) => {
  const features = engagements
    .filter(
      (engagement) =>
        engagement.startMapLocation?.longitude && engagement.startMapLocation?.latitude,
    )
    .map((engagement) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [
          parseFloat(String(engagement.startMapLocation?.longitude)),
          parseFloat(String(engagement.startMapLocation?.latitude)),
        ],
      },
      properties: {
        id: engagement.id,
        title: engagement.title,
        isSelectedDay: selectedDay ? engagement.dayNumber === selectedDay : false,
      },
    }));

  return {
    type: 'FeatureCollection' as const,
    features,
  };
};

// Create GeoJSON for accommodation markers
export const createAccommodationMarkersGeoJSON = (
  accommodations: AccommodationWithDays[],
  selectedDay: number | null,
) => {
  const features = accommodations
    .filter(
      (accommodation) =>
        accommodation.location?.longitude && accommodation.location?.latitude,
    )
    .map((accommodation) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [
          parseFloat(String(accommodation.location.longitude)),
          parseFloat(String(accommodation.location.latitude)),
        ],
      },
      properties: {
        id: accommodation.id,
        title: accommodation.location.name,
        isSelected: selectedDay ? accommodation.daysActive.includes(selectedDay) : false,
      },
    }));

  return {
    type: 'FeatureCollection' as const,
    features,
  };
};
